import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  ClickAwayListener,
  FormHelperText,
  InputAdornment,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppColors } from "../../util/AppColors";

const getOptionKey = (option) =>
  option?.value ?? option?.stockId ?? option?.id ?? option?.name ?? "";

const getMultiOptionKey = (option) =>
  option?.serviceId ??
  option?.value ??
  option?.stockId ??
  option?.id ??
  option?.name ??
  "";

const getOptionLabel = (option) => {
  if (option == null || option === "") return "";
  if (typeof option === "string" || typeof option === "number") {
    return String(option);
  }
  return option?.name ?? "";
};

const getMultiOptionLabel = (option) => {
  if (option == null || option === "") return "";
  if (typeof option === "string" || typeof option === "number") {
    return String(option);
  }
  return option?.name ?? option?.serviceName ?? "";
};

const matchesSearch = (option, searchText) => {
  if (!searchText) return true;
  if (option == null || typeof option !== "object") return false;
  try {
    const haystack = [
      option?._searchText,
      option?.name,
      option?.tradeName,
      option?.productName,
      option?.ingredient,
      option?.serviceName,
      option?.variantName,
    ]
      ?.filter((field) => field != null && field !== "")
      ?.map((field) => String(field).toLowerCase())
      ?.join(" ");
    if (!haystack) return false;
    if (haystack?.includes(searchText)) return true;
    const tokens = searchText.split(/\s+/)?.filter(Boolean);
    return (
      tokens.length > 0 && tokens.every((token) => haystack?.includes(token))
    );
  } catch {
    return false;
  }
};

const buildMultiSearchHaystack = (option, searchFields) => {
  if (option == null || typeof option !== "object") return "";
  return (searchFields ?? [])
    ?.map((field) => option?.[field])
    ?.filter((field) => field != null && field !== "")
    ?.map((field) => String(field).toLowerCase())
    ?.join(" ");
};

const matchesMultiSearch = (option, searchText, searchFields) => {
  if (!searchText) return true;
  if (option == null || typeof option !== "object") return false;
  try {
    const haystack = buildMultiSearchHaystack(option, searchFields);
    if (!haystack) return false;
    if (haystack.includes(searchText)) return true;
    const tokens = searchText.split(/\s+/)?.filter(Boolean);
    return (
      tokens.length > 0 && tokens.every((token) => haystack.includes(token))
    );
  } catch {
    return false;
  }
};

const isSameMultiOption = (option, selected) => {
  if (!option || !selected) return false;
  const optionKey = getMultiOptionKey(option);
  const selectedKey = getMultiOptionKey(selected);
  if (optionKey !== "" && selectedKey !== "") {
    return String(optionKey) === String(selectedKey);
  }
  return option?.name === selected?.name;
};

const resolveValue = (value, list = []) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value;

  const match = (Array.isArray(list) ? list : []).find(
    (opt) =>
      opt?.name === value ||
      opt?.value === value ||
      String(opt?.stockId) === String(value),
  );
  if (match) return match;
  return { name: value, value };
};

/**
 * @param {"default" | "procedure"} variant
 * - default: Inventory-style object value
 * - procedure: accepts current string value style (name), resolves against list
 */
const SearchableSelect = ({
  list = [],
  value = null,
  handleChange,
  label = "",
  error = false,
  helperText = "",
  disabled = false,
  maxFilteredOptions = 100,
  backgroundColor = "transparent",
  variant = "default",
  isForProcedure = false,
  isForBilling = false,
  showQtyNearExpiry = false,
  plusBtn = false,
  searchOnly = false,
  mandatoryField = false,
  multiple = false,
  multiSelectTagCheck = false,
  searchFields = [],
  allowAddNew = false,
  placeholder = "",
}) => {
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const isMulti = multiple || multiSelectTagCheck;
  const isProcedure = variant === "procedure";
  const resolvedValue = useMemo(
    () => (isMulti ? null : resolveValue(value, list)),
    [isMulti, value, list],
  );
  const selectedValues = useMemo(
    () => (isMulti ? (Array.isArray(value) ? value : []) : []),
    [isMulti, value],
  );
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    isMulti ? "" : getOptionLabel(resolveValue(value, list)),
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [anchorWidth, setAnchorWidth] = useState(0);

  useEffect(() => {
    if (!open || !containerRef.current) return;
    setAnchorWidth(containerRef.current.clientWidth);
  }, [open, inputValue]);

  useEffect(() => {
    if (isMulti) {
      if (!open) setInputValue("");
      return;
    }
    if (!open) {
      setInputValue(getOptionLabel(resolvedValue));
    }
  }, [resolvedValue, open, isMulti]);

  const searchableList = useMemo(() => {
    const source = (Array.isArray(list) ? list : [])?.filter(
      (opt) => opt != null && typeof opt === "object",
    );

    if (isMulti) {
      return source?.map((opt) => ({
        ...opt,
        value: opt?.serviceId ?? opt?.stockId ?? opt?.value,
        _searchText:
          opt?._searchText ??
          buildMultiSearchHaystack(opt, searchFields)?.toLowerCase(),
      }));
    }

    return source?.map((opt) => ({
      ...opt,
      value: opt?.stockId ?? opt?.value,
      _searchText:
        opt?._searchText ??
        `${opt?.name ?? ""} ${opt?.productName ?? ""} ${opt?.tradeName ?? ""}`.toLowerCase(),
    }));
  }, [list, isMulti, searchFields]);

  const filteredOptions = useMemo(() => {
    const searchText = inputValue?.trim()?.toLowerCase() ?? "";
    const source = searchableList;
    const limit = maxFilteredOptions ?? source.length;
    const results = [];

    for (let i = 0; i < source.length && results.length < limit; i += 1) {
      const option = source[i];
      const isMatch = isMulti
        ? matchesMultiSearch(option, searchText, searchFields)
        : matchesSearch(option, searchText);
      if (!isMatch) continue;
      results.push(option);
    }

    if (!isMulti) {
      const selectedKey = getOptionKey(resolvedValue);
      if (
        resolvedValue &&
        selectedKey !== "" &&
        !results.some((opt) => getOptionKey(opt) === selectedKey) &&
        matchesSearch(resolvedValue, searchText)
      ) {
        results.unshift(resolvedValue);
        if (results.length > limit) results.length = limit;
      }
    }

    return results;
  }, [
    searchableList,
    inputValue,
    maxFilteredOptions,
    resolvedValue,
    isMulti,
    searchFields,
  ]);

  const selectOption = (option) => {
    handleChange?.(null, option);
    setInputValue(getOptionLabel(option));
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const toggleOption = (option) => {
    const exists = selectedValues.some((item) =>
      isSameMultiOption(item, option),
    );
    const nextValue = exists
      ? selectedValues.filter((item) => !isSameMultiOption(item, option))
      : [...selectedValues, option];
    handleChange?.({ target: { value: nextValue } }, nextValue);
    setHighlightedIndex(-1);
  };

  const removeSelected = (option, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    const nextValue = selectedValues.filter(
      (item) => !isSameMultiOption(item, option),
    );
    handleChange?.({ target: { value: nextValue } }, nextValue);
  };

  const clearSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    if (isMulti) {
      setInputValue("");
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }
    handleChange?.(null, null);
    setInputValue("");
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const addFreeText = () => {
    const text = inputValue?.trim() ?? "";
    if (!text) return;
    selectOption({ name: text, value: text });
  };

  const handleInputChange = (event) => {
    const nextValue = event?.target?.value ?? "";
    setInputValue(nextValue);
    setOpen(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      setOpen(true);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, filteredOptions.length - 1),
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        if (isOptionDisabled(filteredOptions[highlightedIndex])) return;
        if (isMulti) {
          toggleOption(filteredOptions[highlightedIndex]);
        } else {
          selectOption(filteredOptions[highlightedIndex]);
        }
      } else if (
        allowAddNew &&
        isProcedure &&
        !searchOnly &&
        filteredOptions.length === 0 &&
        inputValue?.trim()
      ) {
        addFreeText();
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      if (isMulti) {
        setInputValue("");
      } else {
        setInputValue(getOptionLabel(resolvedValue));
      }
    }
  };

  const showAddNew =
    allowAddNew &&
    isProcedure &&
    !searchOnly &&
    open &&
    filteredOptions.length === 0 &&
    Boolean(inputValue?.trim());

  const closeDropdown = () => {
    setOpen(false);
    if (isMulti) {
      setInputValue("");
    } else {
      setInputValue(getOptionLabel(resolvedValue));
    }
    setHighlightedIndex(-1);
  };

  const showClearIcon = isMulti ? Boolean(inputValue) : Boolean(resolvedValue);

  const isOptionDisabled = (option) =>
    Boolean(option?.alreadyAdded || option?.disabled);

  const renderOptionContent = (option) => {
    if (isMulti) {
      return (
        <>
          <div className="font-medium fs14 black">
            {getMultiOptionLabel(option)}
          </div>
          {option?.category || option?.subCategory ? (
            <div className="font-medium fs12 gray4">
              {[option?.category, option?.subCategory]
                ?.filter(Boolean)
                ?.join(" — ")}
            </div>
          ) : null}
        </>
      );
    }

    if (isForBilling) {
      const isStock = !!option?.stockId;
      const typeLabel = isStock ? "Product" : "Service";
      const displayPrice =
        option?.displayPrice ??
        option?.servicePrice ??
        option?.unitSellingPrice ??
        null;
      const availableQty = option?.availableQty ?? option?.totalQty ?? null;
      const stockStatus = option?.stockStatus ?? null;

      return (
        <>
          <div
            className="flex-row-ali-cen"
            style={{ gap: 8, alignItems: "flex-start" }}
          >
            <div
              className="font-medium fs14 black"
              style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
            >
              {getOptionLabel(option)}
            </div>
            <div className="subcategory-tag white-color font-medium fs10 con-fit">
              {typeLabel}
            </div>
          </div>
          <div
            className="flex-row-ali-cen mt2"
            style={{ gap: 16, flexWrap: "wrap" }}
          >
            {isStock ? (
              <div className="font-medium fs12 gray4">
                Available Qty:{" "}
                {availableQty != null && availableQty !== ""
                  ? availableQty
                  : "-"}
              </div>
            ) : null}
            <div className="font-medium fs12 gray4">
              Price:{" "}
              {displayPrice != null && Number(displayPrice) > 0
                ? `Rs ${Number(displayPrice).toFixed(2)}`
                : "-"}
            </div>
            {isStock &&
            (stockStatus === "expired" || stockStatus === "nearExpiry") ? (
              <div className="flex-row-ali-cen gap4 flex1-end">
                {stockStatus === "expired" ? (
                  <div className="con-fit fs10 white-color ph10 pv2 br10 card-bot-red-back">
                    Expired
                  </div>
                ) : null}
                {stockStatus === "nearExpiry" ? (
                  <div className="con-fit fs10 white-color ph10 pv2 br10 card-top-yellow-color">
                    Near Expiry
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      );
    }

    return (
      <>
        <div className="flex-row">
          <div
            className={
              (isProcedure || isForProcedure) && option?.alreadyAdded
                ? "w75Per"
                : "w100Per"
            }
          >
            <div className="font-medium fs14 black">
              {getOptionLabel(option)}
            </div>
          </div>
          {(isProcedure || isForProcedure) && option?.alreadyAdded ? (
            <div className="w25Per">
              <div className="flex1-end">
                <div className="flex-row-ali-cen gap4 font-bold fs10 whte-spa-no-wrap green2">
                  <CheckIcon
                    sx={{ fontSize: 14, color: AppColors.switchGreen }}
                  />
                  Already Added
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {isProcedure || isForProcedure ? (
          <>
            <div className="font-medium fs12 gray4">
              Generic Name: {option?.productName}
            </div>
            {showQtyNearExpiry ? (
              <div className="font-medium fs10 red2">
                Available Qty:{" "}
                {Number.isFinite(Number(option?.totalQty))
                  ? Number(option.totalQty)
                  : "-"}
              </div>
            ) : null}
            <div className="flex-row-ali-cen gap4 mt2">
              {option?.stockId && option?.subCategory ? (
                <div className="subcategory-tag white-color font-medium fs10 con-fit">
                  {option?.subCategory}
                </div>
              ) : null}
              {option?.stockId &&
              Number.isFinite(Number(option?.totalQty)) &&
              Number(option.totalQty) <= 0 ? (
                <div className="red-tag white-color font-medium fs10 con-fit">
                  Out of Stock
                </div>
              ) : option?.stockId &&
                Number.isFinite(Number(option?.totalQty)) &&
                Number(option.totalQty) > 0 &&
                Number(option.totalQty) <= 10 ? (
                <div className="yellow-tag white-color font-medium fs10 con-fit">
                  Low Stock
                </div>
              ) : null}
              {option?.requestFromPharmacy &&
              !(
                option?.stockId &&
                Number.isFinite(Number(option?.totalQty)) &&
                Number(option.totalQty) <= 0
              ) ? (
                <div className="pharmacy-tag white-color font-medium fs10 con-fit">
                  Requested from Pharmacy
                </div>
              ) : null}
              {showQtyNearExpiry && option?.isNearExpiry ? (
                <div className="org-tag white-color font-medium fs10 con-fit">
                  Near Expiry
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </>
    );
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (dropdownRef.current?.contains(event.target)) return;
        closeDropdown();
      }}
    >
      <div ref={containerRef} style={{ width: "100%" }}>
        <TextField
          fullWidth
          size="small"
          label={label ? (mandatoryField ? `${label} *` : label) : undefined}
          placeholder={isMulti ? "Search" : placeholder || undefined}
          value={inputValue}
          disabled={disabled}
          error={error}
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {showClearIcon ? (
                  <CloseIcon
                    sx={{
                      color: "action.active",
                      cursor: disabled ? "default" : "pointer",
                      fontSize: 18,
                      mr: 0.5,
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      clearSelection(event);
                    }}
                  />
                ) : null}
                <ArrowDropDownIcon
                  sx={{
                    color: "action.active",
                    cursor: disabled ? "default" : "pointer",
                    transform: open ? "rotate(180deg)" : "none",
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    if (disabled) return;
                    setOpen((prev) => !prev);
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ background: backgroundColor }}
        />

        {isMulti && selectedValues.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 1,
            }}
          >
            {selectedValues.map((option) => {
              const chipKey =
                getMultiOptionKey(option) || getMultiOptionLabel(option);
              return (
                <Chip
                  key={chipKey}
                  label={getMultiOptionLabel(option)}
                  size="small"
                  onDelete={
                    disabled
                      ? undefined
                      : (event) => removeSelected(option, event)
                  }
                  sx={{
                    height: 24,
                    backgroundColor: AppColors.lightBlue2,
                    color: AppColors.appPrimary,
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Box>
        ) : null}

        <Popper
          open={open}
          anchorEl={containerRef.current}
          placement="bottom-start"
          disablePortal={false}
          style={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "offset",
              options: { offset: [0, 4] },
            },
            {
              name: "flip",
              enabled: true,
              options: { fallbackPlacements: ["top-start"] },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                boundary: "viewport",
                altAxis: true,
                padding: 8,
              },
            },
          ]}
        >
          <Paper
            ref={dropdownRef}
            elevation={3}
            sx={{
              background: AppColors.white,
              minHeight: 0,
              maxHeight: 280,
              overflowY: "auto",
              overflowX: "hidden",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: "4px",
            }}
            style={{
              width: anchorWidth || containerRef.current?.clientWidth,
              minWidth: anchorWidth || containerRef.current?.clientWidth,
              maxWidth: anchorWidth || containerRef.current?.clientWidth,
            }}
          >
            {filteredOptions.length === 0 ? (
              showAddNew ? (
                <div
                  onMouseDown={(event) => {
                    event.preventDefault();
                    addFreeText();
                  }}
                  style={{
                    padding: "8px 14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 14,
                  }}
                >
                  <span>{inputValue}</span>
                  {plusBtn ? (
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 20 }}
                    />
                  ) : (
                    <span style={{ color: AppColors.appPrimary }}>Add New</span>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    padding: "10px 14px",
                    color: AppColors.gray ?? "#6b7280",
                    fontSize: 14,
                  }}
                >
                  No options
                </div>
              )
            ) : (
              filteredOptions.map((option, index) => {
                const optionKey = isMulti
                  ? getMultiOptionKey(option)
                  : getOptionKey(option);
                const isSelected = isMulti
                  ? selectedValues.some((item) =>
                      isSameMultiOption(item, option),
                    )
                  : resolvedValue != null &&
                    getOptionKey(resolvedValue) === optionKey;
                const isHighlighted = index === highlightedIndex;
                const optionDisabled = isOptionDisabled(option);

                return (
                  <div
                    key={`${optionKey}-${index}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      if (optionDisabled) return;
                      if (isMulti) {
                        toggleOption(option);
                      } else {
                        selectOption(option);
                      }
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    style={{
                      padding: "8px 14px",
                      cursor: optionDisabled ? "not-allowed" : "pointer",
                      opacity: optionDisabled ? 0.65 : 1,
                      background: isHighlighted
                        ? optionDisabled
                          ? "#fafafa"
                          : "#f5f5f5"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                      color: (isMulti
                        ? getMultiOptionLabel(option)
                        : getOptionLabel(option)
                      )
                        ?.toLowerCase()
                        ?.includes("(clinic)")
                        ? AppColors.appPrimary
                        : "inherit",
                      fontSize: 14,
                      display: isMulti ? "flex" : "block",
                      alignItems: isMulti ? "flex-start" : undefined,
                      gap: isMulti ? 8 : undefined,
                    }}
                  >
                    {isMulti ? (
                      isSelected ? (
                        <CheckBoxIcon
                          fontSize="small"
                          sx={{ color: AppColors.appPrimary, mt: "2px" }}
                        />
                      ) : (
                        <CheckBoxOutlineBlankIcon
                          fontSize="small"
                          sx={{ color: AppColors.gray3, mt: "2px" }}
                        />
                      )
                    ) : null}
                    <div style={isMulti ? { flex: 1 } : undefined}>
                      {renderOptionContent(option)}
                    </div>
                  </div>
                );
              })
            )}
          </Paper>
        </Popper>

        {helperText && !open ? (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        ) : null}
      </div>
    </ClickAwayListener>
  );
};

export default SearchableSelect;
